import { z } from 'zod';
import { OFFER_TYPES } from '../offers.js';
import { gstRate, noteText, optionalGstin, optionalPhone, percent, quantity, rupees, shortText, signedQuantity, } from './common.js';
export const STATIONS = ['KITCHEN', 'GRILL', 'FRYER', 'OVEN', 'BEVERAGE', 'BAKERY'];
export const stationEnum = z.enum(STATIONS);
export const UNITS = ['KG', 'LITRE', 'PIECE', 'GRAM', 'ML'];
export const unitEnum = z.enum(UNITS);
/* ---------------- menu ---------------- */
export const createCategorySchema = z.object({
    name: shortText,
    sortOrder: z.number().int().min(0).max(999).default(0),
});
/** Renaming a section, or moving it along the card. Both optional, one needed. */
export const updateCategorySchema = z
    .object({
    name: shortText.optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
})
    .refine((value) => value.name !== undefined || value.sortOrder !== undefined, {
    message: 'Nothing to change',
});
/**
 * The dishes an owner ticked off the starter list.
 *
 * Names rather than ids: the catalogue is written in code, not in the
 * database, so there is nothing to hold an id. The server matches them back
 * against the list for the hotel's own food type, so a name that was never
 * offered - or one from the wrong half of the catalogue - is simply dropped.
 */
export const addStarterMenuSchema = z.object({
    names: z.array(shortText).min(1, 'Choose at least one dish').max(400),
});
/**
 * A new menu item starts free and switched off.
 *
 * The defaults are the product rule, not laziness: an item is created long
 * before anyone has costed it, and a zero-priced item that was live would sell
 * for nothing. `menuItemGoesLive` is the other half - the two are checked
 * together server-side so the UI cannot talk its way past them.
 */
/**
 * Menu item fields without defaults.
 *
 * The update schema is built from these rather than from `createMenuItemSchema`:
 * `.partial()` leaves a field's `.default()` in place, so an edit that sent only
 * a new price would also quietly reset the station, the food type, the SAC code
 * and whether the item was on sale.
 */
const menuItemFields = {
    name: shortText,
    categoryId: z.string().uuid(),
    price: rupees,
    gstRate,
    hsn: z.string().trim().max(10),
    station: stationEnum,
    isVegetarian: z.boolean(),
    /** Standing discount taken off this item on every bill. */
    discountPct: percent,
    isActive: z.boolean(),
};
export const createMenuItemSchema = z.object({
    sku: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9-]{2,16}$/)
        .optional(),
    ...menuItemFields,
    // An item is created long before anybody has costed it, so it starts free and
    // switched off. A zero-priced item that was live would sell for nothing.
    price: rupees.default(0),
    hsn: z.string().trim().max(10).default('996331'),
    station: stationEnum.default('KITCHEN'),
    isVegetarian: z.boolean().default(true),
    discountPct: percent.default(0),
    isActive: z.boolean().default(false),
});
export const updateMenuItemSchema = z
    .object(menuItemFields)
    .partial()
    .extend({ priceChangeReason: noteText });
export const setRecipeSchema = z.object({
    components: z
        .array(z.object({ ingredientId: z.string().uuid(), quantity }))
        .max(40, 'A recipe cannot have more than 40 components'),
});
export const bulkRepriceSchema = z
    .object({
    mode: z.enum(['TARGET_FOOD_COST', 'UPLIFT']),
    categoryId: z.string().uuid().optional(),
    targetFoodCostPct: percent.optional(),
    upliftPct: z.number().min(-50).max(200).optional(),
    reason: noteText,
})
    .refine((v) => v.mode !== 'UPLIFT' || typeof v.upliftPct === 'number', {
    message: 'An uplift percentage is required',
    path: ['upliftPct'],
});
/* ---------------- inventory ---------------- */
export const createIngredientSchema = z.object({
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9-]{2,16}$/)
        .optional(),
    name: shortText,
    unit: unitEnum,
    category: shortText,
    openingStock: z.number().min(0).max(1_000_000).default(0),
    unitCost: rupees.default(0),
    reorderLevel: z.number().min(0).max(1_000_000).default(0),
    parLevel: z.number().min(0).max(1_000_000).default(0),
    gstRate,
    hsn: z.string().trim().max(10).default('2106'),
    shelfLifeDays: z.number().int().min(0).max(3650).default(30),
});
export const updateIngredientSchema = createIngredientSchema
    .partial()
    .omit({ code: true, openingStock: true, unitCost: true });
export const stockMoveSchema = z.object({
    ingredientId: z.string().uuid(),
    type: z.enum(['WASTAGE', 'ADJUSTMENT']),
    quantity: signedQuantity,
    reason: shortText,
    note: noteText,
});
export const stockCountSchema = z.object({
    lines: z
        .array(z.object({ ingredientId: z.string().uuid(), countedQuantity: z.number().min(0) }))
        .min(1),
    note: noteText,
});
/* ---------------- procurement ---------------- */
export const createSupplierSchema = z.object({
    name: shortText,
    gstin: optionalGstin,
    phone: optionalPhone,
    email: z.string().trim().toLowerCase().email().optional().or(z.literal('')),
    address: z.string().trim().max(240).optional().or(z.literal('')),
    paymentTerms: z.enum(['ADVANCE', 'NET_7', 'NET_15', 'NET_30']).default('NET_15'),
    rating: z.number().min(1).max(5).default(4),
    isActive: z.boolean().default(true),
});
/* Defaults re-declared away: Zod 4 applies .default() inside .partial(), so
   an edit that did not send them reset payment terms and rating, and quietly
   re-activated a deactivated supplier. */
export const updateSupplierSchema = createSupplierSchema.partial().extend({
    paymentTerms: z.enum(['ADVANCE', 'NET_7', 'NET_15', 'NET_30']).optional(),
    rating: z.number().min(1).max(5).optional(),
    isActive: z.boolean().optional(),
});
export const createPurchaseOrderSchema = z.object({
    supplierId: z.string().uuid(),
    expectedDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    notes: noteText,
    lines: z
        .array(z.object({
        ingredientId: z.string().uuid(),
        quantity,
        rate: rupees,
        gstRate: gstRate.optional(),
    }))
        .min(1, 'Add at least one line')
        .max(100),
});
export const purchaseOrderActionSchema = z.object({
    action: z.enum(['SUBMIT', 'APPROVE', 'REJECT', 'CANCEL']),
    note: noteText,
});
export const receiveGoodsSchema = z.object({
    supplierInvoiceNo: z.string().trim().max(40).optional().or(z.literal('')),
    receivedAt: z.string().datetime().optional(),
    lines: z
        .array(z.object({
        purchaseOrderLineId: z.string().uuid(),
        quantity: z.number().min(0),
        rate: rupees,
        /**
         * When this delivery goes off.
         *
         * Optional, and defaulted from the item's shelf life when the receipt
         * is posted - the date belongs to the delivery, not the item, because
         * two lots of milk a week apart expire a week apart. Whoever is
         * unpacking the crate can read the printed date and correct it.
         */
        expiryDate: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-27')
            .optional()
            .or(z.literal('')),
    }))
        .min(1),
});
/* ---------------- menu item images ---------------- */
/**
 * What a hotelier may upload as a dish thumbnail.
 *
 * SVG is deliberately absent. An SVG is a document, not just a picture: opened
 * directly it can run script on the API's own origin, which turns an image
 * upload into stored XSS. Seeded artwork is SVG because the server drew it, and
 * every image is served under a locked-down CSP either way.
 */
export const MENU_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** A room photograph answers to the same rules a dish thumbnail does. */
export const ROOM_IMAGE_MIME_TYPES = MENU_IMAGE_MIME_TYPES;
/** A thumbnail, not a photograph: the browser downsizes before it uploads. */
export const MAX_MENU_IMAGE_BYTES = 400 * 1024;
/** Base64 is four characters for every three bytes, plus padding. */
export const base64Bytes = (value) => {
    const clean = value.replace(/\s/g, '');
    const padding = clean.endsWith('==') ? 2 : clean.endsWith('=') ? 1 : 0;
    return Math.max(0, Math.floor((clean.length * 3) / 4) - padding);
};
export const menuItemImageSchema = z.object({
    mime: z.enum(MENU_IMAGE_MIME_TYPES, { message: 'Upload a JPEG, PNG or WebP image' }),
    /** Base64, without a data: prefix — the browser strips it before sending. */
    data: z
        .string()
        .trim()
        .min(32, 'That image is empty')
        .regex(/^[A-Za-z0-9+/=\s]+$/, 'That is not a valid image')
        .refine((value) => base64Bytes(value) <= MAX_MENU_IMAGE_BYTES, `Keep the image under ${Math.round(MAX_MENU_IMAGE_BYTES / 1024)} KB`),
});
/** Same contract for a room photograph; the rules do not differ by subject. */
export const roomImageSchema = menuItemImageSchema;
/**
 * Confirms the decoded bytes actually match the declared mime's file
 * signature - the `mime` field above is otherwise client-declared metadata
 * that is never checked against the content it labels, which would let an
 * uploader store arbitrary bytes under a false image/* label.
 */
export function matchesImageSignature(mime, bytes) {
    switch (mime) {
        case 'image/jpeg':
            return bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
        case 'image/png':
            return (bytes.length >= 8 &&
                bytes[0] === 0x89 &&
                bytes[1] === 0x50 &&
                bytes[2] === 0x4e &&
                bytes[3] === 0x47 &&
                bytes[4] === 0x0d &&
                bytes[5] === 0x0a &&
                bytes[6] === 0x1a &&
                bytes[7] === 0x0a);
        case 'image/webp':
            return (bytes.length >= 12 &&
                bytes[0] === 0x52 && // R
                bytes[1] === 0x49 && // I
                bytes[2] === 0x46 && // F
                bytes[3] === 0x46 && // F
                bytes[8] === 0x57 && // W
                bytes[9] === 0x45 && // E
                bytes[10] === 0x42 && // B
                bytes[11] === 0x50 // P
            );
        default:
            return false;
    }
}
/* ---------------- offers ---------------- */
const dayOfWeek = z.number().int().min(0).max(6);
const minuteOfDay = z.number().int().min(0).max(1439);
const isoDate = z.string().regex(/^d{4}-d{2}-d{2}$/, 'Use a date like 2026-09-30');
/**
 * An offer, whichever of the four shapes it takes.
 *
 * Every type-specific number is optional in the shape and required by the
 * refinements below, so a half-filled form is refused with the field named
 * rather than saved as an offer that quietly never fires.
 */
export const offerSchema = z
    .object({
    name: shortText,
    type: z.enum(OFFER_TYPES),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(999).default(0),
    /** Every item in this category, or the listed items when absent. */
    categoryId: z.string().uuid().nullable().optional(),
    items: z
        .array(z.object({ menuItemId: z.string().uuid(), quantity: z.number().int().min(1).max(20) }))
        .max(20)
        .default([]),
    buyQty: z.number().int().min(1).max(50).optional(),
    freeQty: z.number().int().min(1).max(50).optional(),
    setQty: z.number().int().min(2).max(50).optional(),
    setPrice: rupees.optional(),
    percentOff: z.number().min(0.01).max(100).optional(),
    comboPrice: rupees.optional(),
    minTicket: rupees.default(0),
    maxPerTicket: z.number().int().min(0).max(999).default(0),
    startsOn: isoDate.nullable().optional(),
    endsOn: isoDate.nullable().optional(),
    daysOfWeek: z.array(dayOfWeek).max(7).default([]),
    startMinute: minuteOfDay.nullable().optional(),
    endMinute: minuteOfDay.nullable().optional(),
})
    .refine((v) => Boolean(v.categoryId) || v.items.length > 0, {
    message: 'Choose a section or at least one item',
    path: ['items'],
})
    .refine((v) => v.type !== 'BUY_X_GET_Y' || (v.buyQty !== undefined && v.freeQty !== undefined), {
    message: 'Say how many to buy and how many come free',
    path: ['buyQty'],
})
    .refine((v) => v.type !== 'SET_PRICE' || (v.setQty !== undefined && v.setPrice !== undefined), {
    message: 'Say how many items and what the set costs',
    path: ['setQty'],
})
    .refine((v) => v.type !== 'PERCENT_OFF' || v.percentOff !== undefined, {
    message: 'Say what percentage comes off',
    path: ['percentOff'],
})
    .refine((v) => v.type !== 'COMBO' || (v.comboPrice !== undefined && v.items.length >= 2), {
    message: 'A combo needs at least two items and a price',
    path: ['items'],
})
    .refine((v) => !v.startsOn || !v.endsOn || v.startsOn <= v.endsOn, {
    message: 'The offer cannot end before it starts',
    path: ['endsOn'],
})
    // Both hours or neither: one on its own is a window with no other edge.
    .refine((v) => (v.startMinute === null || v.startMinute === undefined) ===
    (v.endMinute === null || v.endMinute === undefined), {
    message: 'Give both a start and an end time, or neither',
    path: ['endMinute'],
});
/** A promotional code typed on the billing page by a hotel that already exists. */
export const redeemCouponSchema = z.object({
    code: z.string().trim().toUpperCase().min(3).max(40),
});
//# sourceMappingURL=catalog.js.map